import React, {useEffect, useState} from "react";

import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    Switch,
    useMediaQuery,
    useTheme
} from "@material-ui/core";

import Input from "@material-ui/core/Input";
import {useStores} from "../../../../../../hooks";
import {MenuFormType} from "../../../../store/generalMenuStore/forms";
import {observer} from "mobx-react";
import {
    changeCheckboxEventHandlerDecorator,
    changeEventHandlerDecorator,
    localize,
    toLocalizedString,
} from "../../../../../../common/libs";

import {Button} from "../../../../../../common/components/libs";
import {
    CategoryListPhotosMode,
    MenuData,
    MenuInfoMessageMediaType,
    MenuItemsPhotosModes,
    ThemesIds,
    WorkingScheduleDay
} from "../../../../../../services/menu/models";
import {locale} from "../../../../../../common/consts";
import {PaymentProviderType, ServiceFeeType} from "../../../../../../services/general/models";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";


const defaultWorkingSchedule: WorkingScheduleDay = {
    isDayOff: false,
    slots: [
        {
            fromHour: 0,
            fromMinute: 0,
            toHour: 0,
            toMinute: 0
        }
    ]
}

const EditMenuForm = () => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const [name, setName] = useState("");
    const [useIIkoIntegration, setUseIikoIntegration] = useState(false);
    const [apiLogin, setApiLogin] = useState("");
    const [iikoOrgId, setIIkoOrgId] = useState("");
    const [iikoTerminalId, setIIkoTerminalId] = useState("");
    const {t} = useTranslation(["general", "menu"]);


    const { menuStore, generalStore, generalMenuStore } = useStores();
    const currencyId = generalStore.currency.id;
    const forms = generalMenuStore.forms;
    const history = useHistory();
    const { current, open } = forms.forms[MenuFormType.createMenu];

    useEffect(() => {
        setName(current && localize(current.data.name) || "");
    }, [current]);

    const closeForm = () => {
        forms.closeForm(MenuFormType.createMenu);
    };


    const handleSubmit = async () => {
        const data: MenuData = {
            isPromoCodesEnabled: true,
            customersFeedbackRedirections: {},
            externalIntegrationSettings: null,
            reporting: {
                destinations: []
            },
            sortOrder: 0,
            isPublic: true,
            showPhotos: true,
            themeSettings: {
                categoryListPhotosMode: CategoryListPhotosMode.defaultWithPhotos,
                storefrontPhotosMode: MenuItemsPhotosModes.defaultWithPhotos,
                themeId: ThemesIds.default,
                buttonsBackgroundColor: null,
                categoriesBackgroundColor: null,
            },
            deliverySettings: {},
            paymentSettings: {
                [PaymentProviderType.offline]: {
                    externalId: null,
                    isEnabled: true,
                    type: PaymentProviderType.offline,
                    serviceFee: {
                        title: {
                            [locale]: ''
                        },
                        type: ServiceFeeType.fixed,
                        isEnabled: false,
                        amount: {
                            amount: 0,
                            currencyId: currencyId
                        }
                    }
                },
            },
            name: {
                [locale]: name
            },
            description: toLocalizedString(""),
            photos: {
                logoPhotoUrl: null,
                backgroundPhotoUrl: null
            },
            orderCancellationSettings: {
                autoCancelForgottenOrdersAfterSeconds: 0
            },
            contactDetails: {
                websiteUrl: null,
                whatsApp: null,
                telegram: null,
                phoneNumber: null,
                instagram: null
            },
            addressDetails: {
                mapsUrl: null,
                title: toLocalizedString(""),
                region: {
                    ru: "Москва",
                    en: "Moscow"
                }
            },
            workingSchedule: {
                timezoneInMinutes: 180,
                monday: defaultWorkingSchedule,
                tuesday: defaultWorkingSchedule,
                wednesday: defaultWorkingSchedule,
                thursday: defaultWorkingSchedule,
                friday: defaultWorkingSchedule,
                saturday: defaultWorkingSchedule,
                sunday: defaultWorkingSchedule,
            },
            isOrderCreationEnabled: true,
            isCallWaiterEnabled: true,
            isFeedbackEnabled: true,
            isMenuEnabled: true,
            isLoyaltyProgramEnabled: false,
            availableLanguages: {
                zh: {isEnabled: true},
                en: {isEnabled: true},
                it: {isEnabled: true},
                ru: {isEnabled: true},
            },
            infoMessages: [
                {
                    isEnabled: false,
                    title: {
                        zh: "",
                        en: "",
                        it: "",
                        ru: "",
                    },
                    description: {
                        zh: "",
                        en: "",
                        it: "",
                        ru: "",
                    },
                    callToAction: {
                        zh: "",
                        en: "",
                        it: "",
                        ru: "",
                    },
                    mediaItems: [
                        {
                            url: '',
                            mediaType: MenuInfoMessageMediaType.image
                        }
                    ]
                }
            ]
        }
        if(useIIkoIntegration){
            const response = await generalMenuStore.createMenuWithIIkoIntegration({
                apiLogin: apiLogin,
                iikoOrganizationId: iikoOrgId,
                terminalId: iikoTerminalId
            }, data);
            history.push(`/menus/regular/settings/${response.menuId}`);
        }else{
            const response = await generalMenuStore.createMenu(data);
            history.push(`/menus/regular/settings/${response.menuId}`);

        }
        closeForm();
    };


    let title =  t("menu:menusList.menu.creationMenu");

    return (
        <Dialog
            open={open}
            onClose={closeForm}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth={"sm"}
            fullScreen={!isSm}
        >
            <DialogTitle id="form-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <form autoComplete="off">
                    <FormControl fullWidth margin={"normal"}>
                        <InputLabel htmlFor="name">{t("general:name")}</InputLabel>
                        <Input
                            value={name}
                            onChange={changeEventHandlerDecorator(setName)}
                        />
                    </FormControl>

                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useIIkoIntegration}
                                    onChange={changeCheckboxEventHandlerDecorator(setUseIikoIntegration)}
                                    value={useIIkoIntegration}
                                />
                            }
                            label={t("menu:menusList.menu.integration")}
                        />
                    </FormControl>

                    {useIIkoIntegration && <>
                        <FormControl fullWidth margin={"normal"}>
                            <InputLabel htmlFor="name">IIko API Login</InputLabel>
                            <Input
                                value={apiLogin}
                                onChange={changeEventHandlerDecorator(setApiLogin)}
                            />
                        </FormControl>
                        <FormControl fullWidth margin={"normal"}>
                            <InputLabel htmlFor="name">{t("menu:menusList.menu.organizationId")}</InputLabel>
                            <Input
                                value={iikoOrgId}
                                onChange={changeEventHandlerDecorator(setIIkoOrgId)}
                            />
                        </FormControl>
                        <FormControl fullWidth margin={"normal"}>
                            <InputLabel htmlFor="name">{t("menu:menusList.menu.terminalId")}</InputLabel>
                            <Input
                                value={iikoTerminalId}
                                onChange={changeEventHandlerDecorator(setIIkoTerminalId)}
                            />
                        </FormControl>
                    </>
                    }


                </form>
            </DialogContent>

            <DialogActions>
                <Button onClick={closeForm} color="primary">
                    {t("general:cancel")}
                </Button>
                <Button color="primary" onClick={handleSubmit} variant={'contained'}>
                    {t("general:continue")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default observer(EditMenuForm);
